import { isPwa } from "@web/src/utils/pwa";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";

const LAST_ROUTE_KEY = "setlist:lastRoute";
const TRANSIENT_PREFIXES = ["/login", "/auth"];

function isTransientRoute(path: string): boolean {
  return path === "/" || TRANSIENT_PREFIXES.some((p) => path.startsWith(p));
}

/**
 * Persists the current route to localStorage on every navigation,
 * skipping transient pages (splash, login, auth callbacks).
 */
export function usePersistRoute() {
  const router = useRouter();

  useEffect(() => {
    if (!router.isReady) return;
    if (isTransientRoute(router.asPath)) return;
    localStorage.setItem(LAST_ROUTE_KEY, router.asPath);
  }, [router.asPath, router.isReady]);
}

/**
 * Returns the route the PWA should open to on launch.
 *
 * - In PWA mode: the last saved route (or "/setlist" if none).
 * - In browser mode: always "/setlist".
 * - Before client-side resolution: null.
 */
export function usePwaStartRoute(): string | null {
  const [route, setRoute] = useState<string | null>(null);

  useEffect(() => {
    if (!isPwa()) {
      setRoute("/setlist");
      return;
    }
    setRoute(localStorage.getItem(LAST_ROUTE_KEY) ?? "/setlist");
  }, []);

  return route;
}
